import {Notifications} from '@mantine/notifications';
import {memo, useMemo} from 'react';
import type React from 'react';
import type {
  EntryManagementSummaryType,
  LatestStepType,
} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import type {ProgressMissionType} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import type {FilterValues} from '../../../constants/filter';
import type {SortOptionValue} from '../../../constants/sort';
import type {SpStatusFilter} from '../../../usecase/useProgressStatusFilter';
import {ProgressPageHeader} from '../Presenter/Progress/ProgressPageHeader';
import {ProgressKanbanBoard} from './ProgressKanbanBoard';

/**
 * エントリーを5つのカテゴリーに分類する純粋関数
 * @param entries 分類対象のエントリー配列
 * @returns カテゴリー別に分類されたエントリー
 */
const categorizeEntries = (entries: EntryManagementSummaryType[]) => {
  return {
    activeNotCompleted: entries.filter(
      (entry) => entry.overallStatus === 'active' && !entry.hasCompletedEntry,
    ),
    activeCompleted: entries.filter(
      (entry) => entry.overallStatus === 'active' && entry.hasCompletedEntry,
    ),
    completed: entries.filter((entry) => entry.overallStatus === 'completed'),
    archived: entries.filter((entry) => entry.overallStatus === 'archived'),
    rejected: entries.filter((entry) => entry.overallStatus === 'rejected'),
  };
};

export type ProgressPageComponentProps = {
  entryManagementId?: number;
  companySearchWord: string;
  setCompanySearchWord: (value: string) => void;
  onCompanySearchSubmit?: (value: string) => void;
  handleSort: (sort: SortOptionValue) => void;
  /** ログイン状態をチェックし、未ログイン時はモーダルを表示する */
  requireAuth?: () => boolean;
  handleFilter: (filter: FilterValues) => void;
  companyList: string[];
  filteredEntries: EntryManagementSummaryType[];
  /** APIレスポンスのtotalCount（SP版で件数表示に使用） */
  totalCount?: number;
  reminderTexts: string[];
  selectedLatestStep?: LatestStepType;
  handleAddEntryManagement: () => void;
  progressMissions: ProgressMissionType[];
  onUpdateEntryStatus?: (params: {
    id: number;
    overallStatus: string;
    hasCompletedEntry?: boolean;
  }) => Promise<void>;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  /** SP版ステータスフィルターの選択値 */
  selectedStatus?: SpStatusFilter;
  /** SP版ステータスフィルターの変更ハンドラ */
  onStatusChange?: (status: SpStatusFilter) => void;
  /** AI ToDo生成モーダルを開くハンドラ */
  handleAiTodoGenerate?: () => void;
  /** カードから詳細を開く前のチェック。falseを返すと遷移を中断する */
  canOpenDetail?: () => boolean;
};

// PC版コンポーネント（進捗ページ共通）- React.memoで最適化
const ProgressPagePcComponentBase: React.FC<ProgressPageComponentProps> = ({
  entryManagementId,
  companySearchWord,
  setCompanySearchWord,
  onCompanySearchSubmit,
  handleSort,
  requireAuth,
  companyList,
  filteredEntries,
  reminderTexts,
  handleAddEntryManagement,
  onUpdateEntryStatus,
  handleAiTodoGenerate,
  canOpenDetail,
}) => {
  // エントリーを5つのカテゴリーに分類（メモ化して再計算を防ぐ）
  const categorizedEntries = useMemo(
    () => categorizeEntries(filteredEntries),
    [filteredEntries],
  );

  return (
    <>
      <Notifications position="top-right" />
      <div className="flex flex-col gap-16">
        <ProgressPageHeader
          reminderTexts={reminderTexts}
          companyList={companyList}
          companySearchWord={companySearchWord}
          onCompanySearchChange={setCompanySearchWord}
          onCompanySearchSubmit={onCompanySearchSubmit}
          onSort={handleSort}
          requireAuth={requireAuth}
          onAddEntry={handleAddEntryManagement}
          onAiTodoGenerate={handleAiTodoGenerate}
        />
        <div className="bg-white rounded-16 p-16 overflow-x-auto">
          <ProgressKanbanBoard
            {...categorizedEntries}
            entryManagementId={entryManagementId}
            canOpenDetail={canOpenDetail}
            onUpdateEntryStatus={
              onUpdateEntryStatus ||
              (async () => {
                // onUpdateEntryStatusが提供されていない場合の警告
                // 実装されていない状態でドラッグ&ドロップが実行された場合のフォールバック
                console.warn(
                  'onUpdateEntryStatus is not implemented. Drag and drop will not update the entry status.',
                );
              })
            }
          />
        </div>
      </div>
    </>
  );
};

// React.memoでラップしてエクスポート
export const ProgressPagePcComponent = memo(ProgressPagePcComponentBase);
